import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import {
  ClipboardDocumentListIcon, UserGroupIcon,
  TagIcon, BuildingOfficeIcon, XMarkIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, CardHeader, CardTitle, Button, Input, Select, Textarea,
  PageLoader, Avatar, Badge, StatusBadge
} from '../../components/ui';
import { taskService, projectService } from '../../services';

export default function TaskForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, projectId: urlProjectId } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loadingProject, setLoadingProject] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      project: urlProjectId || '',
      title: '',
      description: '',
      status: 'todo',
      priority: 'medium',
      assignees: [],
      startDate: '',
      dueDate: '',
      estimatedHours: 0,
      milestone: '',
      tags: [],
      isClientVisible: false
    }
  });

  const selectedProjectId = watch('project');
  const selectedAssignees = watch('assignees') || [];
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchProjects();
    if (isEdit) {
      fetchTask();
    } else if (urlProjectId) {
      fetchProjectDetails(urlProjectId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, urlProjectId]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchProjectDetails(selectedProjectId);
    } else {
      setSelectedProject(null);
      setProjectMembers([]);
      setMilestones([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  const fetchProjects = async () => {
    try {
      const response = await projectService.getProjects({ limit: 100 });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    setLoadingProject(true);
    try {
      const project = await projectService.getProject(projectId);
      setSelectedProject(project);

      const ownerId = project.owner?._id?.toString();
      const uniqueMembers = project.members
        .map(m => m.user)
        .filter(member => member && member._id?.toString() !== ownerId);
      const allMembers = [project.owner, ...uniqueMembers].filter(Boolean);
      setProjectMembers(allMembers);
      setMilestones(project.milestones || []);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoadingProject(false);
    }
  };

  const fetchTask = async () => {
    try {
      const task = await taskService.getTask(id);
      reset({
        project: task.project?._id || '',
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'todo',
        priority: task.priority || 'medium',
        assignees: task.assignees?.map(a => a._id) || [],
        startDate: task.startDate ? dayjs(task.startDate).format('YYYY-MM-DD') : '',
        dueDate: task.dueDate ? dayjs(task.dueDate).format('YYYY-MM-DD') : '',
        estimatedHours: task.estimatedHours || 0,
        milestone: task.milestone?._id || '',
        tags: task.tags || [],
        isClientVisible: task.isClientVisible || false
      });

      if (task.project?._id) {
        await fetchProjectDetails(task.project._id);
      }
    } catch (error) {
      toast.error(t('errors.notFound'));
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    if (!data.project) {
      toast.error(t('tasks.selectProject') || 'Please select a project');
      return;
    }

    setSubmitting(true);
    try {
      const taskData = {
        title: data.title,
        description: data.description || undefined,
        status: data.status,
        priority: data.priority,
        assignees: data.assignees || [],
        startDate: data.startDate || undefined,
        dueDate: data.dueDate || undefined,
        estimatedHours: data.estimatedHours || 0,
        milestone: data.milestone || undefined,
        tags: data.tags || [],
        isClientVisible: data.isClientVisible || false
      };

      if (isEdit) {
        await taskService.updateTask(id, taskData);
        toast.success(t('success.updated'));
      } else {
        await taskService.createTask(data.project, taskData);
        toast.success(t('success.created'));
      }

      navigate('/tasks');
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const currentTags = watch('tags') || [];
      if (!currentTags.includes(tagInput.trim())) {
        setValue('tags', [...currentTags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    const currentTags = watch('tags') || [];
    setValue('tags', currentTags.filter(tag => tag !== tagToRemove));
  };

  const toggleAssignee = (userId) => {
    const currentAssignees = watch('assignees') || [];
    if (currentAssignees.includes(userId)) {
      setValue('assignees', currentAssignees.filter(id => id !== userId));
    } else {
      setValue('assignees', [...currentAssignees, userId]);
    }
  };

  const projectOptions = projects.map(project => ({
    value: project._id,
    label: project.name
  }));

  const statusOptions = [
    { value: 'todo', label: t('tasks.statuses.todo') },
    { value: 'in_progress', label: t('tasks.statuses.inProgress') },
    { value: 'review', label: t('tasks.statuses.review') },
    { value: 'completed', label: t('tasks.statuses.completed') },
    { value: 'cancelled', label: t('tasks.statuses.cancelled') }
  ];

  const priorityOptions = [
    { value: 'low', label: t('projects.priorities.low') },
    { value: 'medium', label: t('projects.priorities.medium') },
    { value: 'high', label: t('projects.priorities.high') },
    { value: 'urgent', label: t('projects.priorities.urgent') }
  ];

  const milestoneOptions = [
    { value: '', label: t('common.none') || 'None' },
    ...milestones.map(milestone => ({
      value: milestone._id,
      label: milestone.title
    }))
  ];

  if (loading) {
    return <PageLoader />;
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? t('tasks.editTask') : t('tasks.newTask')} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={isEdit ? t('tasks.editTask') : t('tasks.newTask')}
        backUrl="/tasks"
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Project Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BuildingOfficeIcon className="w-5 h-5 text-primary" />
                {t('projects.title')}
              </CardTitle>
            </CardHeader>
            <CardBody>
              <Controller
                name="project"
                control={control}
                rules={{ required: t('errors.required') }}
                render={({ field }) => (
                  <Select
                    label={t('projects.title')}
                    options={projectOptions}
                    placeholder={t('tasks.selectProject') || 'Select a project'}
                    error={errors.project?.message}
                    disabled={isEdit || !!urlProjectId}
                    {...field}
                  />
                )}
              />
              {selectedProject && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-medium">{selectedProject.name}</p>
                      <p className="text-sm text-gray-500">
                        {selectedProject.client?.name || t('projects.noClientSelected')}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <StatusBadge status={selectedProject.status} />
                        <span className="text-xs text-gray-500">
                          {t('common.progress')}: {selectedProject.progress}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {selectedProjectId && (
            <>
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardDocumentListIcon className="w-5 h-5 text-primary" />
                    {t('tasks.taskDetails') || 'Task Details'}
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="space-y-4">
                    <Input
                      label={t('common.name') || 'Title'}
                      placeholder={t('tasks.titlePlaceholder') || 'Enter task title'}
                      error={errors.title?.message}
                      {...register('title', { required: t('errors.required') })}
                    />

                    <Textarea
                      label={t('common.description')}
                      rows={4}
                      placeholder={t('tasks.descriptionPlaceholder') || 'Enter task description'}
                      error={errors.description?.message}
                      {...register('description')}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('common.status')}
                            options={statusOptions}
                            error={errors.status?.message}
                            {...field}
                          />
                        )}
                      />

                      <Controller
                        name="priority"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('common.priority')}
                            options={priorityOptions}
                            error={errors.priority?.message}
                            {...field}
                          />
                        )}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Assignment & Dates */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserGroupIcon className="w-5 h-5 text-primary" />
                    {t('tasks.assignees')} & {t('common.date')}
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="space-y-4">
                    {/* Assignees */}
                    <div>
                      <label className="label mb-2">{t('tasks.assignees')}</label>
                      {loadingProject ? (
                        <p className="text-sm text-gray-500">{t('common.loading')}...</p>
                      ) : projectMembers.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {projectMembers.map((member) => {
                            const isSelected = selectedAssignees.includes(member._id);
                            return (
                              <button
                                key={member._id}
                                type="button"
                                onClick={() => toggleAssignee(member._id)}
                                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${isSelected
                                    ? 'border-primary bg-primary/10'
                                    : 'border-gray-200 hover:border-gray-300'
                                  }`}
                              >
                                <Avatar
                                  src={member.avatar ? (member.avatar.startsWith('http') ? member.avatar : process.env.REACT_APP_SOCKET_URL + member.avatar) : null}
                                  name={`${member.firstName} ${member.lastName}`}
                                  size="sm"
                                />
                                <div className="flex-1 text-left">
                                  <p className="font-medium text-sm">
                                    {member.firstName} {member.lastName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {member._id === selectedProject?.owner?._id ? t('projects.owner') : 'Member'}
                                  </p>
                                </div>
                                {isSelected && (
                                  <CheckCircleIcon className="w-5 h-5 text-primary" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">{t('tasks.noTeamMembers') || 'No team members available'}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label={t('projects.startDate')}
                        type="date"
                        error={errors.startDate?.message}
                        {...register('startDate')}
                      />

                      <Input
                        label={t('tasks.dueDate')}
                        type="date"
                        error={errors.dueDate?.message}
                        {...register('dueDate')}
                      />
                    </div>

                    <Input
                      label={t('tasks.estimatedHours')}
                      type="number"
                      step="0.5"
                      min="0"
                      placeholder="0"
                      error={errors.estimatedHours?.message}
                      {...register('estimatedHours', {
                        min: { value: 0, message: t('errors.minValue', { min: 0 }) }
                      })}
                    />
                  </div>
                </CardBody>
              </Card>

              {/* Additional Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TagIcon className="w-5 h-5 text-primary" />
                    {t('common.additionalInfo') || 'Additional Information'}
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="space-y-4">
                    {milestones.length > 0 && (
                      <Controller
                        name="milestone"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('projects.milestones')}
                            options={milestoneOptions}
                            error={errors.milestone?.message}
                            {...field}
                          />
                        )}
                      />
                    )}

                    {/* Tags */}
                    <div>
                      <label className="label mb-2">{t('common.tags')}</label>
                      <Input
                        placeholder={t('projects.addTagPlaceholder') || 'Add tag and press Enter'}
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagKeyDown}
                      />
                      {watch('tags') && watch('tags').length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {watch('tags').map((tag, index) => (
                            <Badge key={index} variant="neutral" className="flex items-center gap-1">
                              {tag}
                              <button
                                type="button"
                                onClick={() => removeTag(tag)}
                                className="ml-1 hover:text-red-600"
                              >
                                <XMarkIcon className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Client Visibility */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="isClientVisible"
                        {...register('isClientVisible')}
                        className="rounded"
                      />
                      <label htmlFor="isClientVisible" className="text-sm text-gray-700 cursor-pointer">
                        {t('tasks.makeVisibleToClient') || 'Make visible to client'}
                      </label>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/tasks')}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={submitting || !selectedProjectId}
            >
              {submitting ? t('common.loading') : (isEdit ? t('common.update') : t('common.create'))}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}